/**
 * Reusable markdown styling utility
 * Extracted from v3/scribe patterns to ensure consistent markdown styling across the application
 */

import { SxProps, Theme } from '@mui/material'

/**
 * Standard markdown styles based on the patterns used in the scribe module
 * These styles are designed to work with the MuiMarkdown component
 */
export let markdownStyles: SxProps<Theme> = {
  '& h1': {
    marginTop: 2,
    marginBottom: 1,
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  '& h2': {
    marginTop: 1.5,
    marginBottom: 1,
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  '& h3': {
    marginTop: 1.5,
    marginBottom: 0.75,
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  '& h4': {
    marginTop: 1.25,
    marginBottom: 0.75,
    fontSize: '1.1rem',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  '& h5': {
    marginTop: 1.5,
    marginBottom: 1,
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  '& h6': {
    marginTop: 1.5,
    marginBottom: 1,
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  '& p': {
    marginTop: 0,
    marginBottom: 1.2,
    lineHeight: 1.7,
    fontSize: '0.95rem',
  },
  '& ul': {
    paddingLeft: 2,
    marginTop: 0,
    marginBottom: 1.5,
    listStyleType: 'disc',
    '& li': {
      marginBottom: 0.8,
      lineHeight: 1.6,
      display: 'list-item',
    },
  },
  '& ol': {
    paddingLeft: 2,
    marginTop: 0,
    marginBottom: 1.5,
    listStyleType: 'decimal',
    '& li': {
      marginBottom: 0.8,
      lineHeight: 1.6,
      display: 'list-item',
    },
  },
  '& blockquote': {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderLeft: '4px solid',
    borderLeftColor: 'primary.main',
    padding: 1.5,
    margin: '1rem 0',
    borderRadius: '0 4px 4px 0',
    fontStyle: 'italic',
    '& p': {
      margin: 0,
    },
  },
  '& hr': {
    margin: '2rem 0',
    borderColor: 'divider',
  },
  '& strong': {
    fontWeight: 600,
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& code': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '2px 6px',
    borderRadius: 1,
    fontSize: '0.875rem',
  },
  '& pre': {
    backgroundColor: 'background.paper',
    padding: 2,
    borderRadius: 1,
    margin: '1rem 0',
    overflow: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '1rem 0',
    '& th, & td': {
      border: '1px solid',
      borderColor: 'divider',
      padding: 1,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: 'background.paper',
      fontWeight: 600,
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 1,
    margin: '1rem 0',
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}

/**
 * Lightweight markdown styles for inline content or smaller contexts
 */
export let lightMarkdownStyles: SxProps<Theme> = {
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: 1,
    marginBottom: 0.5,
  },
  '& h1': { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2 },
  '& h2': { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.2 },
  '& h3': { fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.3 },
  '& p': {
    marginTop: 0,
    marginBottom: 1,
    lineHeight: 1.6,
  },
  '& ul, & ol': {
    paddingLeft: 1.5,
    marginTop: 0,
    marginBottom: 1,
    '& li': {
      marginBottom: 0.25,
      lineHeight: 1.6,
    },
  },
  '& strong': {
    fontWeight: 600,
  },
  '& code': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '1px 4px',
    borderRadius: 0.5,
    fontSize: '0.85rem',
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}
